// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import type { ReviewRunInspection } from "./api.js";

export function ReviewerProfileEvidence({
  inspection,
}: {
  readonly inspection: ReviewRunInspection;
}) {
  const selected = inspection.run.reviewerProfile;
  const executions = inspection.outcome?.profileExecutions ?? [];
  const results = inspection.outcome?.obligationResults ?? [];
  const profiles = (inspection.plan?.protocolSelections ?? []).filter(
    (selection) => selection.reviewerProfilePackage !== undefined,
  );
  if (selected === undefined && profiles.length === 0) return null;
  return (
    <details className="disclosure">
      <summary>Reviewer profile and skill evidence</summary>
      {selected === undefined ? null : (
        <div className="fact-list">
          <p>
            {selected.reference.protocolId} ·{" "}
            {selected.source === "explicit"
              ? "Selected for this run"
              : "Project default"}
          </p>
          <p className="hint">
            Policy revision {selected.projectPolicyRevision} · Package{" "}
            {selected.reference.contentDigest}
          </p>
        </div>
      )}
      {profiles.length === 0 ? (
        <p className="hint">
          The selected profile has not been resolved. No skill execution is
          established.
        </p>
      ) : null}
      {profiles.map((selection) => {
        const bundle = selection.reviewerProfilePackage;
        if (bundle === undefined) return null;
        const reviewer = bundle.manifest.reviewer;
        const attempts = executions.filter(
          (attempt) => attempt.protocolId === selection.protocolId,
        );
        return (
          <section
            className="stack"
            key={selection.protocolId}
            aria-label={`Profile ${selection.protocolId}`}
          >
            <h4>
              {selection.protocolId}@{selection.protocolVersion}
            </h4>
            <p>
              <strong>Scope:</strong> {reviewer.scope}
            </p>
            <p>
              <strong>Writing style:</strong> {reviewer.writingGuidance}
            </p>
            <p className="hint">
              Up to {reviewer.execution.maximumSteps} steps per stage and{" "}
              {reviewer.execution.maximumCandidates} candidates.
            </p>
            {reviewer.skills.length === 0 ? (
              <p>No skills are declared in this profile.</p>
            ) : (
              <ul className="fact-list" aria-label="Selected skills">
                {reviewer.skills.map((skill) => (
                  <li className="finding-card" key={skill.id}>
                    <strong>{skill.id}</strong>
                    <p className="hint">
                      Included in the approved bundle · Requires{" "}
                      {skill.requiredCapabilities.join(", ") ||
                        "no additional capabilities"}
                      {skill.requiredTools.length === 0
                        ? ""
                        : ` · Tools: ${skill.requiredTools.join(", ")}`}
                    </p>
                    {skill.stageIds.map((stageId) => {
                      const loaded = attempts.filter(
                        (attempt) =>
                          attempt.evidence.stageId === stageId &&
                          attempt.evidence.skillLoads.some(
                            (load) => load.skillId === skill.id,
                          ),
                      );
                      return (
                        <p key={stageId}>
                          {stageId}:{" "}
                          {loaded.length === 0
                            ? "No successful load recorded"
                            : `Loaded in ${String(loaded.length)} execution attempt(s)`}
                        </p>
                      );
                    })}
                  </li>
                ))}
              </ul>
            )}
            <h4>Required checks</h4>
            {reviewer.obligations.length === 0 ? (
              <p>No checks are declared.</p>
            ) : (
              <ul className="fact-list" aria-label="Required checks">
                {reviewer.obligations.map((obligation) => {
                  const result = results.find(
                    (item) =>
                      item.protocolId === selection.protocolId &&
                      item.obligationId === obligation.id,
                  );
                  return (
                    <li className="finding-card" key={obligation.id}>
                      <strong>{obligation.description}</strong>
                      <p>
                        {obligation.skillId} · {obligation.stageId}:{" "}
                        {result?.state === "complete"
                          ? "Complete"
                          : result?.state === "incomplete"
                            ? "Incomplete"
                            : "Completion not established"}
                      </p>
                      {result?.missingEvidence.length ? (
                        <p className="hint">
                          Missing: {result.missingEvidence.join("; ")}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {selection.knownLimitations.length === 0 ? null : (
              <p className="hint">
                Limitations: {selection.knownLimitations.join("; ")}
              </p>
            )}
            {attempts.map((attempt) => (
              <details className="disclosure" key={attempt.executionAttemptId}>
                <summary>
                  {attempt.role} · {attempt.evidence.stageId} · Observed
                  activity
                </summary>
                <p className="hint">
                  Attempt {attempt.executionAttemptId} · Receipt{" "}
                  {attempt.evidenceReceiptDigest}
                </p>
                {(attempt.evidence.activities ?? []).length === 0 ? (
                  <p>No tool activity recorded.</p>
                ) : (
                  <ul>
                    {attempt.evidence.activities?.map((activity) => (
                      <li key={activity.activityId}>
                        {activity.kind === "command-execution"
                          ? `Command exited ${String(activity.exitCode)}`
                          : `${activity.kind === "reference-read" ? `Reference ${activity.complete ? "fully" : "partially"} read` : "Repository file read"}: ${activity.path}`}
                      </li>
                    ))}
                  </ul>
                )}
                {attempt.claims.map((claim) => (
                  <p key={claim.obligationId}>
                    Agent claim for {claim.obligationId}: {claim.state}.{" "}
                    {claim.reasoning}
                  </p>
                ))}
                {attempt.assessments.map((assessment) => (
                  <p
                    key={`${assessment.executionAttemptId}:${assessment.obligationId}`}
                  >
                    Assessment for {assessment.obligationId}: {assessment.state}
                    . {assessment.reasoning}
                  </p>
                ))}
                {attempt.limitations.length === 0 ? null : (
                  <p className="hint">
                    Limitations: {attempt.limitations.join("; ")}
                  </p>
                )}
              </details>
            ))}
          </section>
        );
      })}
    </details>
  );
}
